/**
 * Cell styles and the interned `StyleTable`.
 *
 * Each cell stores a single 32-bit style id in the columnar `styleIds` array of
 * its chunk (see `src/core/store/chunk.ts`); the style itself lives once in the
 * workbook's `StyleTable`. A range of one million identically formatted cells
 * therefore costs one table entry plus the id column, which is what makes
 * Requirement 6.3 affordable inside the 2 GB budget of Requirement 14.6.
 *
 * Interning is by canonical key, so `internStyle` is idempotent: applying the
 * same patch to two ranges yields the same id, and the id is stable across the
 * session. Style 0 is the default style and number format 0 is `general`, which
 * is why an untouched cell can leave its style id at 0 and cost nothing.
 *
 * Stored styles are frozen. Callers derive a new style with
 * {@link applyStylePatch} rather than mutating one, so an id always denotes the
 * same appearance for the lifetime of the workbook.
 */

import {
  DEFAULT_FONT_SIZE_PX,
  GENERAL_FORMAT,
  numberFormat,
  numberFormatKey,
  type NumberFormat,
} from './numberFormat.js';

/** Horizontal alignment. `general` means "numbers right, text left". */
export type HAlign = 'general' | 'left' | 'center' | 'right';

export type VAlign = 'top' | 'middle' | 'bottom';

/** Border line styles the renderer can draw. */
export type BorderLine = 'none' | 'thin' | 'medium' | 'thick' | 'dashed' | 'dotted' | 'double';

export interface BorderSide {
  line: BorderLine;
  /** CSS colour. */
  color: string;
}

export interface CellBorders {
  top: BorderSide;
  right: BorderSide;
  bottom: BorderSide;
  left: BorderSide;
}

/**
 * Everything Requirement 6.3 lists, plus the number-format id.
 *
 * The number format is part of the style rather than a second per-cell column:
 * one id per cell keeps the store layout unchanged, and the format itself is
 * interned separately so the same format shared by many styles costs one entry.
 */
export interface CellStyle {
  /** Index into {@link StyleTable.numberFormats}. */
  numFmtId: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  fontSizePx: number;
  /** Text colour, CSS notation. */
  color: string;
  /** Background fill, CSS notation. `'transparent'` means no fill. */
  background: string;
  hAlign: HAlign;
  vAlign: VAlign;
  wrap: boolean;
  borders: CellBorders;
}

/**
 * A partial style, as carried by the `applyStyle` command.
 *
 * Borders are patchable per side, so "add a bottom border" does not have to
 * restate the other three. Any `Partial<CellStyle>` is a valid patch.
 */
export type CellStylePatch = Omit<Partial<CellStyle>, 'borders'> & {
  borders?: Partial<CellBorders>;
};

export const NO_BORDER: BorderSide = Object.freeze({ line: 'none' as BorderLine, color: '#000000' });

export const NO_BORDERS: CellBorders = Object.freeze({
  top: NO_BORDER,
  right: NO_BORDER,
  bottom: NO_BORDER,
  left: NO_BORDER,
});

/** Style id 0: the appearance of a cell nobody has formatted. */
export const DEFAULT_STYLE: CellStyle = Object.freeze({
  numFmtId: 0,
  bold: false,
  italic: false,
  underline: false,
  fontSizePx: DEFAULT_FONT_SIZE_PX,
  color: '#000000',
  background: 'transparent',
  hAlign: 'general' as HAlign,
  vAlign: 'bottom' as VAlign,
  wrap: false,
  borders: NO_BORDERS,
});

/** Style id every unformatted cell carries. */
export const DEFAULT_STYLE_ID = 0;

/** Number-format id of the `general` format. */
export const GENERAL_FORMAT_ID = 0;

export interface StyleTable {
  /** Interned styles; index 0 is {@link DEFAULT_STYLE}. */
  styles: CellStyle[];
  /** Interned number formats; index 0 is `general`. */
  numberFormats: NumberFormat[];
  /** Canonical key to style id. */
  styleIndex: Map<string, number>;
  /** Canonical key to number-format id. */
  formatIndex: Map<string, number>;
}

function borderKey(side: BorderSide): string {
  return `${side.line}:${side.color}`;
}

/** Canonical key for interning. Field order is fixed, so the key is stable. */
export function styleKey(style: CellStyle): string {
  return [
    style.numFmtId,
    style.bold ? 'b' : '-',
    style.italic ? 'i' : '-',
    style.underline ? 'u' : '-',
    style.fontSizePx,
    style.color,
    style.background,
    style.hAlign,
    style.vAlign,
    style.wrap ? 'w' : '-',
    borderKey(style.borders.top),
    borderKey(style.borders.right),
    borderKey(style.borders.bottom),
    borderKey(style.borders.left),
  ].join('\u0001');
}

export function sameStyle(a: CellStyle, b: CellStyle): boolean {
  return styleKey(a) === styleKey(b);
}

export function createStyleTable(): StyleTable {
  const table: StyleTable = {
    styles: [DEFAULT_STYLE],
    numberFormats: [GENERAL_FORMAT],
    styleIndex: new Map([[styleKey(DEFAULT_STYLE), DEFAULT_STYLE_ID]]),
    formatIndex: new Map([[numberFormatKey(GENERAL_FORMAT), GENERAL_FORMAT_ID]]),
  };
  return table;
}

/** Interns a number format, returning its id. Idempotent. */
export function internNumberFormat(table: StyleTable, fmt: NumberFormat): number {
  const normalized = numberFormat(fmt);
  const key = numberFormatKey(normalized);
  const existing = table.formatIndex.get(key);
  if (existing !== undefined) return existing;
  const id = table.numberFormats.length;
  table.numberFormats.push(Object.freeze(normalized));
  table.formatIndex.set(key, id);
  return id;
}

/** The number format behind an id. Unknown ids resolve to `general`. */
export function numberFormatById(table: StyleTable, id: number): NumberFormat {
  return table.numberFormats[id] ?? GENERAL_FORMAT;
}

/** Interns a style, returning its id. Idempotent. */
export function internStyle(table: StyleTable, style: CellStyle): number {
  const key = styleKey(style);
  const existing = table.styleIndex.get(key);
  if (existing !== undefined) return existing;
  const id = table.styles.length;
  table.styles.push(freezeStyle(style));
  table.styleIndex.set(key, id);
  return id;
}

/** The style behind an id. Unknown ids resolve to the default style. */
export function styleById(table: StyleTable, id: number): CellStyle {
  return table.styles[id] ?? DEFAULT_STYLE;
}

/** The number format a style id implies. */
export function numberFormatOfStyleId(table: StyleTable, styleId: number): NumberFormat {
  return numberFormatById(table, styleById(table, styleId).numFmtId);
}

function freezeStyle(style: CellStyle): CellStyle {
  return Object.freeze({
    ...style,
    borders: Object.freeze({
      top: Object.freeze({ ...style.borders.top }),
      right: Object.freeze({ ...style.borders.right }),
      bottom: Object.freeze({ ...style.borders.bottom }),
      left: Object.freeze({ ...style.borders.left }),
    }),
  });
}

/**
 * The style that results from applying `patch` to `base`.
 *
 * Absent fields are inherited, which is what makes "toggle bold on a range"
 * leave every other attribute of every cell alone (Requirement 6.3, Property 20).
 */
export function applyStylePatch(base: CellStyle, patch: CellStylePatch): CellStyle {
  const borders: CellBorders = {
    top: patch.borders?.top ?? base.borders.top,
    right: patch.borders?.right ?? base.borders.right,
    bottom: patch.borders?.bottom ?? base.borders.bottom,
    left: patch.borders?.left ?? base.borders.left,
  };
  return {
    numFmtId: patch.numFmtId ?? base.numFmtId,
    bold: patch.bold ?? base.bold,
    italic: patch.italic ?? base.italic,
    underline: patch.underline ?? base.underline,
    fontSizePx: patch.fontSizePx ?? base.fontSizePx,
    color: patch.color ?? base.color,
    background: patch.background ?? base.background,
    hAlign: patch.hAlign ?? base.hAlign,
    vAlign: patch.vAlign ?? base.vAlign,
    wrap: patch.wrap ?? base.wrap,
    borders,
  };
}

/** Interns `base` with `patch` applied, returning the resulting id. */
export function patchedStyleId(table: StyleTable, styleId: number, patch: CellStylePatch): number {
  return internStyle(table, applyStylePatch(styleById(table, styleId), patch));
}
